import { useState } from 'react';
import { attendanceAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Clock, Download, Loader2, Search } from 'lucide-react';
import { cn } from '../../lib/utils';

const SUMMARY_FIELDS = [
  ['total_scheduled_hours', 'Scheduled Hrs', 'text-sky-400'],
  ['total_actual_hours', 'Actual Hrs', 'text-emerald-400'],
  ['total_normal_hours', 'Normal Hrs', 'text-foreground'],
  ['total_overtime_hours', 'Overtime Hrs', 'text-orange-400'],
  ['total_late_deduction_minutes', 'Late (min)', 'text-amber-400'],
  ['total_absence_hours', 'Absence Hrs', 'text-red-400'],
  ['total_annual_leave_hours', 'Annual Leave', 'text-emerald-300'],
  ['total_sick_leave_hours', 'Sick Leave', 'text-rose-400'],
  ['total_unpaid_leave_hours', 'Unpaid Leave', 'text-slate-400'],
  ['total_public_holidays', 'Public Holidays', 'text-purple-300'],
  ['exception_count', 'Exceptions', 'text-red-400'],
  ['total_payable_hours', 'Payable Hrs', 'text-primary'],
];

export function AttendanceHistory({ employeeId }) {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState(null);

  const validate = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both a start and end date');
      return false;
    }
    if (startDate > endDate) {
      toast.error('Start date cannot be after end date');
      return false;
    }
    return true;
  };

  const handleView = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await attendanceAPI.getEmployeeReport(employeeId, startDate, endDate);
      setReport(res.data);
    } catch (e) {
      toast.error('Failed to load attendance history');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!validate()) return;
    setExporting(true);
    try {
      const res = await attendanceAPI.exportEmployeeReport(employeeId, startDate, endDate);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const name = report?.employee?.full_name?.replace(/\s+/g, '_') || employeeId;
      link.setAttribute('download', `attendance_${name}_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel report downloaded');
    } catch (e) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ta-history-section">
      <div className="glass-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-semibold">Time &amp; Attendance History</h3>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1">
            <Label htmlFor="ta-start">Start Date</Label>
            <Input
              id="ta-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-44"
              data-testid="ta-start-date"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ta-end">End Date</Label>
            <Input
              id="ta-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-44"
              data-testid="ta-end-date"
            />
          </div>
          <Button onClick={handleView} disabled={loading} data-testid="ta-view-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            View Attendance
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exporting}
            data-testid="ta-export-btn"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export to Excel
          </Button>
        </div>
      </div>

      {report && (
        <>
          {/* Summary */}
          <div className="glass-card p-5">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold">Period Summary</h4>
              <span className="text-sm text-muted-foreground">
                {report.period.start} → {report.period.end}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ta-summary-grid">
              {SUMMARY_FIELDS.map(([key, label, color]) => (
                <div key={key} className="rounded-lg bg-secondary/40 p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className={cn('text-lg font-bold', color)} data-testid={`ta-summary-${key}`}>
                    {report.summary[key] ?? 0}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detail table */}
          <div className="glass-card table-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Day</TableHead>
                  <TableHead>Scheduled Shift</TableHead>
                  <TableHead>Sched Start</TableHead>
                  <TableHead>Sched End</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Normal</TableHead>
                  <TableHead>Overtime</TableHead>
                  <TableHead>Late (min)</TableHead>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>Pub. Hol.</TableHead>
                  <TableHead>Absence</TableHead>
                  <TableHead>Missing</TableHead>
                  <TableHead>Exception</TableHead>
                  <TableHead>Corrected</TableHead>
                  <TableHead>Payable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={17} className="text-center py-10 text-muted-foreground">
                      No attendance records for the selected period
                    </TableCell>
                  </TableRow>
                ) : (
                  report.rows.map((row, idx) => (
                    <TableRow key={`${row.shift_date || row.date}-${idx}`} data-testid={`ta-row-${row.shift_date || row.date}`}>
                      <TableCell>{row.shift_date || row.date}</TableCell>
                      <TableCell>{row.day_of_week}</TableCell>
                      <TableCell>
                        {row.shift_template_name ? (
                          <Badge variant="outline" className="bg-purple-500/15 text-purple-300 border-purple-500/30 text-xs">
                            {row.shift_template_name}
                          </Badge>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{row.scheduled_start || '-'}</TableCell>
                      <TableCell>{row.scheduled_end || '-'}</TableCell>
                      <TableCell className={cn(!row.first_clock_in && 'text-muted-foreground')}>
                        {row.first_clock_in?.slice(0, 5) || '-'}
                      </TableCell>
                      <TableCell className={cn(!row.last_clock_out && 'text-muted-foreground')}>
                        {row.last_clock_out?.slice(0, 5) || '-'}
                      </TableCell>
                      <TableCell>{(row.ordinary_hours ?? 0) > 0 ? row.ordinary_hours.toFixed(2) : '-'}</TableCell>
                      <TableCell>
                        {(row.overtime_hours ?? 0) > 0 ? <span className="text-orange-400">{row.overtime_hours.toFixed(2)}</span> : '-'}
                      </TableCell>
                      <TableCell>
                        {row.late_deduction_minutes > 0 ? <span className="text-amber-400">{row.late_deduction_minutes}</span> : '-'}
                      </TableCell>
                      <TableCell>{row.leave_type || '-'}</TableCell>
                      <TableCell>{row.is_public_holiday ? <Badge variant="outline" className="bg-purple-500/20 text-purple-300 border-purple-500/30">Yes</Badge> : '-'}</TableCell>
                      <TableCell>
                        {(row.absent_unpaid_hours ?? 0) > 0 ? <span className="text-red-400">{row.absent_unpaid_hours.toFixed(2)}</span> : '-'}
                      </TableCell>
                      <TableCell>{row.missing_clocking ? <Badge variant="destructive">Yes</Badge> : '-'}</TableCell>
                      <TableCell className="max-w-[180px] truncate" title={(row.exceptions || []).join(', ')}>
                        {(row.exceptions && row.exceptions.length) ? (row.exceptions.join(', ')) : (row.exception_status || '-')}
                      </TableCell>
                      <TableCell>{row.is_corrected ? <Badge variant="outline" className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Yes</Badge> : '-'}</TableCell>
                      <TableCell className="font-semibold">
                        {(row.total_payable_hours ?? 0) > 0 ? row.total_payable_hours.toFixed(2) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
